/**
 * WorkspaceEditFrame.js
 *
 * Edit-mode chrome around a widget: a dashed orange outline plus a compact tool
 * row - move up / down (reorder), size S / M / L, settings and remove. The widget
 * stays visible (and live) underneath, but is not tappable while editing.
 *
 * @module components/workspaces/WorkspaceEditFrame
 */

import React from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import haptics from '../../helpers/haptics';
import {WIDGET_SIZES} from '../../config/workspaceCatalog';

const ToolButton = ({icon, label, onPress, disabled, color}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel={label}
            style={[styles.tool, {backgroundColor: m.colors.surfaceAlt, opacity: disabled ? 0.35 : 1}]}
        >
            <Icon name={icon} size={17} color={color || m.colors.heading} />
        </TouchableOpacity>
    );
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.children The widget.
 * @param {string} props.size Current size.
 * @param {boolean} props.isFirst Hides "up".
 * @param {boolean} props.isLast Hides "down".
 * @param {(direction: number) => void} props.onMove Move -1 / +1.
 * @param {(size: string) => void} props.onResize Pick a size.
 * @param {() => void} props.onSettings Open the settings sheet.
 * @param {() => void} props.onRemove Remove the widget.
 * @returns {JSX.Element} The framed widget.
 */
export default function WorkspaceEditFrame({children, size, isFirst, isLast, onMove, onResize, onSettings, onRemove}) {
    const m = useManagementTheme();
    const {t} = useTranslation();

    return (
        <View style={[styles.frame, {borderColor: m.colors.primary}]}>
            <View pointerEvents="none">{children}</View>
            <View style={styles.tools}>
                <ToolButton icon="arrow-up" label={t('wsd_move_up')} disabled={isFirst} onPress={() => { haptics.select(); onMove(-1); }} />
                <ToolButton icon="arrow-down" label={t('wsd_move_down')} disabled={isLast} onPress={() => { haptics.select(); onMove(1); }} />
                <View style={[styles.sizes, {backgroundColor: m.colors.surfaceAlt}]}>
                    {WIDGET_SIZES.map((option) => (
                        <TouchableOpacity
                            key={option}
                            onPress={() => { haptics.select(); onResize(option); }}
                            accessibilityRole="button"
                            accessibilityState={{selected: option === size}}
                            accessibilityLabel={t(`wsd_size_${option.toLowerCase()}`)}
                            style={[styles.size, option === size && {backgroundColor: m.colors.primary}]}
                        >
                            <Text style={[styles.sizeText, {color: option === size ? '#FFFFFF' : m.colors.subText}]}>{option}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
                <ToolButton icon="tune-variant" label={t('wsd_widget_settings')} onPress={onSettings} />
                <ToolButton icon="trash-can-outline" label={t('wsd_remove_widget')} color={m.colors.danger} onPress={onRemove} />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    frame: {borderWidth: 1.5, borderStyle: 'dashed', borderRadius: 22, padding: 6, marginBottom: 12},
    tools: {flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: 2, paddingBottom: 4},
    tool: {width: 34, height: 34, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},
    sizes: {flexDirection: 'row', borderRadius: 12, padding: 3},
    size: {width: 28, height: 28, borderRadius: 9, alignItems: 'center', justifyContent: 'center'},
    sizeText: {fontSize: 12, fontWeight: '800'},
});
